import {useEffect, useState, useRef} from 'react'
import {usePercentagePopup} from '../../../store/hooks/percentagePopup'
import ReactDOM from 'react-dom'
import {
  MainBoxPopupWrapper,
  StyledAlert,
  StyledPopupContainer,
  StyledFAQLink,
  StyledFAQSectionText
} from './percentagePopupStyle'
import ContentComponent from './ContentComponent'
import IconComponent from './IconComponent'

const LOCAL_STORAGE_KEY = 'percentagePopupShown'

const ANIMATION_DURATION = 1500 // ms, must match CSS

const PercentagePopup = () => {
  const {popupOpen, setPopupOpen} = usePercentagePopup()
  const [remaining, setRemaining] = useState(8000)
  const [shouldRender, setShouldRender] = useState(false)
  const [localOpen, setLocalOpen] = useState(false)
  const timerRef = useRef(null)
  const startTimeRef = useRef(null)
  const popupRef = useRef(null)
  const closeTimeoutRef = useRef(null)

  // Timer effect: controls auto-close, only show if not already shown
  useEffect(() => {
    if (!localStorage.getItem(LOCAL_STORAGE_KEY)) {
      setShouldRender(true)
      setLocalOpen(false)
      // Force a reflow to ensure Chrome registers the transition
      requestAnimationFrame(() => {
        // Add a short delay to guarantee browser registers initial state
        setTimeout(() => {
          setLocalOpen(true)
          setPopupOpen(true)
        }, 50)
      })
      startTimeRef.current = Date.now()
      timerRef.current = setTimeout(() => setPopupOpen(false), remaining)
      localStorage.setItem(LOCAL_STORAGE_KEY, 'true')
    }
    return () => {
      clearTimeout(timerRef.current)
      clearTimeout(closeTimeoutRef.current)
    }
    // eslint-disable-next-line
  }, [])

  // Unmount after exit animation
  useEffect(() => {
    if (popupOpen) {
      setShouldRender(true)
      setLocalOpen(true)
      clearTimeout(closeTimeoutRef.current)
    } else if (shouldRender) {
      setLocalOpen(false)
      closeTimeoutRef.current = setTimeout(() => setShouldRender(false), ANIMATION_DURATION)
    }
    return () => clearTimeout(closeTimeoutRef.current)
  }, [popupOpen, shouldRender])

  // Click-away effect: only active when open
  useEffect(() => {
    if (!popupOpen) return
    const handleClickAway = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setPopupOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickAway)
    document.addEventListener('touchstart', handleClickAway)
    return () => {
      document.removeEventListener('mousedown', handleClickAway)
      document.removeEventListener('touchstart', handleClickAway)
    }
  }, [popupOpen, setPopupOpen])

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return
    setPopupOpen(false)
  }

  // Pause timer on hover
  const handleMouseEnter = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      const elapsed = Date.now() - startTimeRef.current
      setRemaining((prev) => prev - elapsed)
    }
  }

  // Resume timer on mouse leave
  const handleMouseLeave = () => {
    startTimeRef.current = Date.now()
    timerRef.current = setTimeout(() => setPopupOpen(false), remaining)
  }

  if (!shouldRender) return null
  return ReactDOM.createPortal(
    <StyledPopupContainer open={localOpen}>
      <MainBoxPopupWrapper ref={popupRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <StyledAlert onClose={handleClose} severity='info' icon={<IconComponent />}>
          <ContentComponent />
          <StyledFAQSectionText>
            Visit our <StyledFAQLink href='/faq'>FAQs</StyledFAQLink> section for more Information.
          </StyledFAQSectionText>
        </StyledAlert>
      </MainBoxPopupWrapper>
    </StyledPopupContainer>,
    document.body
  )
}

export default PercentagePopup
